import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import RegistrationForm from './components/RegistrationForm';
import ValueProposition from './components/ValueProposition';
import SecurityIndicators from './components/SecurityIndicators';
import SEOHead from '../../components/SEOHead';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import { authService } from '../../services/authService';

const UserRegistration = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Get trip context from location state
  const tripData = location?.state?.tripData;
  const fromTripConfirmation = location?.state?.fromTripConfirmation;

  useEffect(() => {
    // Enhanced redirect logic for trip confirmation flow
    if (user && fromTripConfirmation && tripData) {
      // User has successfully registered and we have trip data
      // Redirect them back to trip confirmation to complete the flow
      navigate('/trip-confirmation', {
        state: { tripData },
        replace: true
      });
    } else if (user && !fromTripConfirmation) {
      // Normal registration flow - go to dashboard
      const from = location?.state?.from?.pathname || '/user-dashboard';
      navigate(from, { replace: true });
    }
  }, [user, navigate, location, fromTripConfirmation, tripData]);

  const handleRegister = async (formData) => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const { data, error: signUpError } = await authService?.signUp({
        email: formData?.email,
        password: formData?.password,
        fullName: formData?.firstName
      });

      if (signUpError) {
        setError(signUpError?.message || 'Registration failed. Please try again.');
        return;
      }

      // Redirect to login with verification pending message
      navigate('/user-login', {
        state: {
          emailSent: true,
          email: formData?.email,
          ...(fromTripConfirmation && tripData ? {
            returnTo: '/trip-confirmation',
            tripData
          } : {})
        },
        replace: true
      });
    } catch (err) {
      console.error('Registration error:', err);
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialSignup = async (provider) => {
    setIsLoading(true);
    setError(null);

    try {
      // For now, show message that social signup will be available later
      setError(`${provider} signup will be available soon. Please use email registration.`);
    } catch (err) {
      setError(`${provider} signup failed. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  // Add this block - Define individual handlers for social providers
  const handleGoogleSignup = () => handleSocialSignup('Google');
  const handleFacebookSignup = () => handleSocialSignup('Facebook');

  if (success) {
    return (
      <div className="min-h-screen bg-background">
        <Header isAuthenticated={false} user={null} onLogout={() => {}} />
        
        <main className="pt-16">
          <div className="min-h-screen flex items-center justify-center p-8">
            <div className="w-full max-w-md text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-success rounded-organic flex items-center justify-center shadow-organic-md">
                  <Icon name="CheckCircle" size={32} color="white" strokeWidth={2} />
                </div>
              </div>
              
              <h1 className="font-heading font-heading-bold text-3xl text-foreground mb-4">
                {fromTripConfirmation ? 'Account Created!' : 'Check Your Email'}
              </h1>
              
              <p className="font-body text-muted-foreground mb-6">
                {fromTripConfirmation ?
                "Great! Your account has been created. Once you verify your email, you can sign in and save your trip itinerary." :
                "We've sent you a confirmation link. Please check your email and click the link to verify your account before signing in."
                }
              </p>
              
              <div className="bg-accent/10 border border-accent/20 rounded-organic p-4 mb-6">
                <p className="text-sm text-accent">
                  <strong>{fromTripConfirmation ? 'Next Steps:' : 'Important:'}</strong>{' '}
                  {fromTripConfirmation ?
                  "Verify your email, then return and sign in to save your trip permanently." :
                  "The verification link will redirect to localhost (this is normal). Your account verification will be completed successfully. Simply return to this platform to sign in."
                  }
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => navigate('/user-login', {
                    state: fromTripConfirmation ? {
                      returnTo: '/trip-confirmation',
                      tripData
                    } : undefined
                  })}
                  className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-organic hover:bg-primary/90 transition-colors font-body font-body-medium">
                  {fromTripConfirmation ? 'Go to Sign In' : 'Back to Sign In'}
                </button>
                
                {fromTripConfirmation &&
                <button
                  onClick={() => navigate('/trip-confirmation', { state: { tripData } })}
                  className="w-full py-2 text-sm text-muted-foreground hover:text-card-foreground transition-colors">
                    Return to Trip Summary
                  </button>
                }
              </div>
            </div>
          </div>
        </main>
      </div>);

  }

  return (
    <>
      <SEOHead
        pageType="register"
        title="Create Account - Start Planning Adventures | ParkTrip AI"
        description="Join ParkTrip Planner AI to save unlimited trip itineraries, get AI-powered recommendations, and plan unforgettable outdoor adventures."
        canonicalUrl="https://parktripplannerai.com/user-registration"
        keywords="state park registration, outdoor adventure planning, trip planner account"
        ogImage="https://parktripplannerai.com/images/og-register.jpg"
        structuredData={{}}
      />

      <div className="min-h-screen bg-background">
        <Header isAuthenticated={false} user={null} onLogout={() => {}} />

        <main className="pt-16">
          <div className="min-h-screen flex">
            {/* Left Side - Hero Image with Why Create Account Section */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20"></div>
              <Image
                src="https://images.unsplash.com/photo-1666160399564-8774cf0e70bd"
                alt="Majestic mountain landscape with snow-capped peaks reflected in crystal clear lake at sunrise"
                className="w-full h-full object-cover" />


              <div className="absolute inset-0 bg-black/30"></div>
              
              {/* Why Create an Account Section - Floating over image */}
              <div className="absolute inset-0 flex items-center justify-center p-8">
                <div className="w-full max-w-lg">
                  <ValueProposition />
                </div>
              </div>
            </div>

            {/* Right Side - Registration Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
              <div className="w-full max-w-md">
                <div className="text-center mb-8">
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 bg-primary rounded-organic flex items-center justify-center shadow-organic-md">
                      <Icon name={fromTripConfirmation ? "Save" : "Trees"} size={32} color="white" strokeWidth={2} />
                    </div>
                  </div>
                  <h1 className="font-heading font-heading-bold text-3xl text-foreground mb-2">
                    {fromTripConfirmation ? 'Save Your Trip' : 'Create Your Account'}
                  </h1>
                  <p className="font-body text-muted-foreground">
                    {fromTripConfirmation ?
                    "Create an account to save your itinerary and access it anytime" : "Join ParkTrip Planner AI and start saving and planning your perfect state park adventures!"
                    }
                  </p>
                </div>

                {/* Trip Context Banner */}
                {fromTripConfirmation && tripData &&
                <div className="bg-primary/10 border border-primary/20 rounded-organic p-4 mb-6">
                    <div className="flex items-center space-x-3">
                      <Icon name="MapPin" size={16} color="var(--color-primary)" />
                      <div>
                        <p className="text-sm font-body font-body-medium text-card-foreground">
                          {tripData?.featuredParks?.[0] || 'State Park'} Adventure
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {tripData?.duration || '3-day'} • {tripData?.activityTypes?.length || 0} activities
                        </p>
                      </div>
                    </div>
                  </div>
                }

                <RegistrationForm
                  onRegister={handleRegister}
                  onRegisterSuccess={() => setSuccess(true)}
                  onRegisterError={(err) => setError(err)}
                  isLoading={isLoading}
                  error={error}
                  onGoogleSignup={handleGoogleSignup}
                  onFacebookSignup={handleFacebookSignup}
                />

                <SecurityIndicators />
                
                {fromTripConfirmation &&
                <div className="mt-6 pt-4 border-t border-border">
                    <button
                    onClick={() => navigate('/trip-confirmation', { state: { tripData } })}
                    className="w-full py-2 text-sm text-muted-foreground hover:text-primary transition-colors">

                      ← Back to Trip Summary
                    </button>
                  </div>
                }
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default UserRegistration;